import React, { useEffect } from "react";
import { Box, Paper } from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";

import ToolBar from "./Toolbar";

const Tiptap = ({ content = "", onChange, editable = true, minHeight = 300 }) => {
  const isFirstUpdate = React.useRef(true);
  const editor = useEditor({
    editable,

    extensions: [
      StarterKit,

      Highlight.configure({
        multicolor: true,
      }),

      Underline,

      Link.configure({
        openOnClick: false,
      }),

      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],

    content,

    editorProps: {
      attributes: {
        class: "ProseMirror",
      },
    },

    onUpdate({ editor }) {
      if (isFirstUpdate.current) {
        isFirstUpdate.current = false;
        return;
      }
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  /**
   * Keep editor editable state in sync.
   */
  useEffect(() => {
    if (!editor) return;

    editor.setEditable(editable);
  }, [editable, editor]);

  /**
   * Sync external value (Cancel / Redux updates).
   */
  useEffect(() => {
    if (!editor) return;

    const normalize = (html) => (html === "<p></p>" ? "" : html);
    const current = normalize(editor.getHTML());
    const incoming = normalize(content || "");
    if (current !== incoming) {
      editor.commands.setContent(content || "", false); // false = don't emit update
    }
  }, [content, editor]);

  if (!editor) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderRadius: 1,
      }}
    >
      {editable && <ToolBar editor={editor} />}

      <Box
        sx={{
          p: 2,

          backgroundColor: editable ? "#fff" : "#f5f5f5",

          "& .ProseMirror": {
            minHeight,
            outline: "none",
            wordBreak: "break-word",

            "& p": {
              margin: "8px 0",
            },

            "& ul": {
              paddingLeft: 3,
            },

            "& ol": {
              paddingLeft: 3,
            },

            "& blockquote": {
              borderLeft: "4px solid #ddd",
              paddingLeft: 12,
              margin: "12px 0",
              color: "#666",
            },

            "& pre": {
              background: "#272822",
              color: "#fff",
              padding: 12,
              borderRadius: 4,
              overflowX: "auto",
            },

            "& code": {
              background: "#f5f5f5",
              padding: "2px 4px",
              borderRadius: 3,
            },

            "& a": {
              color: "#1976d2",
            },

            "& table": {
              borderCollapse: "collapse",
              width: "100%",
              marginTop: 8,
              marginBottom: 8,
            },

            "& th, & td": {
              border: "1px solid #ccc",
              padding: "8px",
              verticalAlign: "top",
            },

            "& th": {
              backgroundColor: "#f5f5f5",
              fontWeight: 600,
            },

            "& .selectedCell": {
              backgroundColor: "#dbeafe",
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
};

export default React.memo(Tiptap);
